export interface IDocumentTokenFieldType {
  token: string;
  labelKey: string;
  icon: string;
  color: string;
  defaultW: number; // mm
  defaultH: number; // mm
}

/** Convert font size in pt to mm (exact: 1pt = 25.4/72 mm) */
export const PT_TO_MM = 25.4 / 72;

/** Sentinel written when user picks "Platzhalter" before choosing a real path */
export const PLACEHOLDER_SENTINEL = "{{placeholder}}";

/** Value stored in a checkbox token when checked */
export const CHECKBOX_CHECKED_VALUE = "x";
/** Value stored in a checkbox token when unchecked */
export const CHECKBOX_UNCHECKED_VALUE = "";

/** Returns true for any {{...}} token that is not a fixed sign/signature token */
export function isPlaceholderToken(token: string): boolean {
  return token.startsWith("{{") && token !== "{{sign.qr}}" && token !== "{{sign.img}}" && token !== "{{signature}}";
}

export const DOCUMENT_TOKEN_FIELD_TYPES: IDocumentTokenFieldType[] = [
  {
    token: "{{sign.qr}}",
    labelKey: "sign.fieldType.signatureQr",
    icon: "mdi-draw",
    color: "blue-grey darken-1",
    defaultW: 60,
    defaultH: 30
  },
  {
    token: "{{sign.img}}",
    labelKey: "sign.fieldType.signatureImg",
    icon: "mdi-signature-freehand",
    color: "blue-grey darken-1",
    defaultW: 45,
    defaultH: 20
  },
  {
    token: PLACEHOLDER_SENTINEL,
    labelKey: "sign.fieldType.placeholder",
    icon: "mdi-variable",
    color: "grey",
    defaultW: 52,
    defaultH: 4.23
  },
  {
    token: "value",
    labelKey: "sign.fieldType.value",
    icon: "mdi-form-textbox",
    color: "blue-grey",
    defaultW: 63,
    defaultH: 4.23
  },
  {
    token: "X",
    labelKey: "sign.fieldType.checkbox",
    icon: "mdi-checkbox-marked-outline",
    color: "grey darken-1",
    defaultW: 2.5,
    defaultH: 2.5
  }
];

const TOKEN_DISPLAY: Record<string, Pick<IDocumentTokenFieldType, "color" | "labelKey">> = {
  "{{sign.qr}}": { color: "blue-grey darken-1", labelKey: "sign.fieldType.signatureQr" },
  "{{sign.img}}": { color: "blue-grey darken-1", labelKey: "sign.fieldType.signatureImg" },
  "{{signature}}": { color: "blue-grey darken-1", labelKey: "sign.fieldType.signatureQr" },
  X: { color: "grey darken-1", labelKey: "sign.fieldType.checkbox" },
  value: { color: "blue-grey", labelKey: "sign.fieldType.value" }
};

export function colorForToken(token: string): string {
  if (isPlaceholderToken(token)) return "grey";
  return TOKEN_DISPLAY[token]?.color ?? "grey";
}

export function labelKeyForToken(token: string): string {
  if (isPlaceholderToken(token)) return "sign.fieldType.placeholder";
  return TOKEN_DISPLAY[token]?.labelKey ?? token;
}

export function displayLabelForToken(token: string, translate: (key: string) => string): string {
  if (!token) return "";
  const stripped = token.replaceAll("{", "").replaceAll("}", "").trim();
  if (stripped.includes("#") && stripped.includes(" ") && stripped.includes("/")) {
    const bits = stripped.split(" ");
    const formatter = bits[0].replaceAll("#", "");
    const path = bits[1] ?? "";
    const pathLabel = translate(`components.template.editor.placeholder.path.${path}`) || path;
    const formatLabel = translate(`components.template.editor.placeholder.formatters.${formatter}`) || formatter;
    return `${pathLabel} ${formatLabel}`.trim();
  }
  if (isPlaceholderToken(token) && token !== PLACEHOLDER_SENTINEL) {
    return translate(`components.template.editor.placeholder.path.${stripped}`) || stripped;
  }
  const labelKey = labelKeyForToken(token);
  return labelKey !== token ? translate(labelKey) || token : token;
}
